import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { getInstanceSetting } from 'api/admin';
import {
	defaultTicketTypeMap,
	getTicketTypeMeta as getDefaultTicketTypeMeta,
	mergeTerminology,
	TerminologyOverride,
	TicketTypeMeta,
} from '../utils/getTicketTypeMeta';

const SETTING_APP = 'canpark';
const SETTING_KEY = 'ticket_terminology';

type GetTicketTypeMeta = (ticketType: string) => TicketTypeMeta;

const TicketTerminologyContext = createContext<GetTicketTypeMeta>(
	getDefaultTicketTypeMeta,
);

// Parse the raw instance-setting value into a terminology override. Any problem
// (missing, empty, non-JSON, non-object) silently yields no override → defaults.
function parseOverride(raw: string | null | undefined): TerminologyOverride | null {
	if (!raw || !raw.trim()) {
		console.log(
			'[TicketTerminology] No override value present (null/empty) → using defaults',
		);
		return null;
	}
	try {
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
			console.warn(
				'[TicketTerminology] Override is not a JSON object → using defaults. Got:',
				parsed,
			);
			return null;
		}
		console.log('[TicketTerminology] Parsed override:', parsed);
		return parsed as TerminologyOverride;
	} catch (err) {
		console.warn(
			'[TicketTerminology] Failed to JSON.parse override → using defaults. Raw value:',
			raw,
			err,
		);
		return null;
	}
}

/**
 * Loads the per-tenant `canpark / ticket_terminology` instance setting once and
 * exposes a `getTicketTypeMeta`-compatible getter whose label text reflects the
 * client's wording. Falls back to the built-in Record/Warning/Tow defaults while
 * loading or on any error.
 */
export const TicketTerminologyProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const { data, status, error, fetchStatus } = useQuery({
		queryKey: ['instance-setting', SETTING_APP, SETTING_KEY],
		queryFn: () => getInstanceSetting(SETTING_APP, SETTING_KEY),
		staleTime: 5 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
		retry: false,
	});

	useEffect(() => {
		console.log(
			`[TicketTerminology] query "${SETTING_APP}/${SETTING_KEY}" status=${status} fetchStatus=${fetchStatus}`,
			{ value: data, error },
		);
		if (status === 'success') {
			if (data == null) {
				console.warn(
					`[TicketTerminology] Setting "${SETTING_APP}/${SETTING_KEY}" was NOT found (returned null). ` +
						'Check the app/key spelling in /instance-settings.',
				);
			} else {
				console.log(
					`[TicketTerminology] Setting "${SETTING_APP}/${SETTING_KEY}" READ ok. Raw value:`,
					data,
				);
			}
		}
	}, [data, status, fetchStatus, error]);

	const getMeta = useMemo<GetTicketTypeMeta>(() => {
		const map = mergeTerminology(parseOverride(data));
		console.log('[TicketTerminology] Active labels:', {
			log: { short: map.log.shortLabel, action: map.log.buttonText },
			logAndWarn: {
				short: map.logAndWarn.shortLabel,
				action: map.logAndWarn.buttonText,
			},
			tow: { short: map.tow.shortLabel, action: map.tow.buttonText },
		});
		return (ticketType: string) =>
			map[ticketType as keyof typeof map] ??
			getDefaultTicketTypeMeta(ticketType);
	}, [data]);

	return (
		<TicketTerminologyContext.Provider value={getMeta}>
			{children}
		</TicketTerminologyContext.Provider>
	);
};

/**
 * Returns a getter with the same signature as the old `getTicketTypeMeta(type)`,
 * but with the active tenant's terminology applied. Safe to call anywhere inside
 * the provider; outside it (no provider) it returns built-in defaults.
 */
export function useTicketTypeMeta(): GetTicketTypeMeta {
	return useContext(TicketTerminologyContext);
}

// Re-exported for callers that only need the default shape.
export { defaultTicketTypeMap };
